import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Plus, Pencil, Trash2, DollarSign, TrendingUp, Upload, FileDown } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useFilters } from "@/context/FilterContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { BlockSkeleton } from "@/components/Loading";
import { BrandSelectField } from "@/components/BrandSelectField";
import { ExportButton } from "@/components/ExportButton";
import { KpiCard } from "@/components/KpiCard";
import { ChartCard, ChartTooltip, CHART_COLORS, PIE_COLORS } from "@/components/charts";
import { fmtCurrency, fmtCurrencyCents, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const CATEGORIES = ["total", "food", "beverage", "events", "lodging", "other"];
const axisStyle = { fontSize: 12, fill: "rgba(48,56,60,0.62)" };
const shortDate = (d) => { const dt = new Date(d); return isNaN(dt) ? d : dt.toLocaleDateString("en-US", { month: "short", day: "numeric" }); };
const today = () => new Date().toISOString().slice(0, 10);

const empty = { brand_id: "", date: today(), amount: "", category: "total", notes: "" };

export default function Revenue() {
  const { queryParams, brands } = useFilters();
  const { isCorporate } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importBrand, setImportBrand] = useState("all");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const brandName = useMemo(() => Object.fromEntries(brands.map((b) => [b.id, b.name])), [brands]);
  const nonCorpBrands = useMemo(() => brands.filter((b) => !b.is_corporate), [brands]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/revenue", { params: queryParams })
      .then((res) => setItems(res.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [queryParams]);

  useEffect(() => { load(); }, [load]);

  const total = useMemo(() => items.reduce((s, r) => s + Number(r.amount || 0), 0), [items]);
  const avg = items.length ? total / new Set(items.map((r) => r.date)).size : 0;

  const trend = useMemo(() => {
    const byDay = {};
    items.forEach((r) => { byDay[r.date] = (byDay[r.date] || 0) + Number(r.amount || 0); });
    return Object.keys(byDay).sort().map((d) => ({ date: d, revenue: Math.round(byDay[d]) }));
  }, [items]);

  const byCategory = useMemo(() => {
    const m = {};
    items.forEach((r) => { const c = r.category || "total"; m[c] = (m[c] || 0) + Number(r.amount || 0); });
    return Object.keys(m).sort((a, b) => m[b] - m[a]).map((c) => ({ name: c, value: Math.round(m[c]) }));
  }, [items]);

  const downloadTemplate = () => {
    const header = "date,amount,category,brand,notes";
    const sample = [
      "2026-08-01,12500.50,total,37 North at Fort Monroe,Saturday dinner service",
      "2026-08-01,3400.00,lodging,37 North at Fort Monroe,Room revenue",
      "2026-08-02,9800.00,food,River Stone Chophouse,",
    ];
    const blob = new Blob([[header, ...sample].join("\n")], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "packbrothers_revenue_template.csv";
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const runImport = async () => {
    if (!importFile) { toast.error("Choose a CSV or Excel file first"); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      if (isCorporate && importBrand && importBrand !== "all") fd.append("default_brand_id", importBrand);
      const res = await api.post("/revenue/import", fd);
      setImportResult(res.data);
      if (res.data.inserted > 0) {
        toast.success(`Imported ${res.data.inserted} entr${res.data.inserted === 1 ? "y" : "ies"}` + (res.data.skipped ? `, ${res.data.skipped} skipped` : ""));
        load();
      } else {
        toast.error("No rows imported. Check the errors below.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const openCreate = () => { setForm({ ...empty, date: today() }); setEditingId(null); setDialogOpen(true); };
  const openEdit = (r) => { setForm({ ...r, amount: String(r.amount) }); setEditingId(r.id); setDialogOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (isNaN(payload.amount)) { toast.error("Enter a valid amount"); setSaving(false); return; }
      if (editingId) { await api.patch(`/revenue/${editingId}`, payload); toast.success("Revenue updated"); }
      else { await api.post("/revenue", payload); toast.success("Revenue entry added"); }
      setDialogOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/revenue/${deleteId}`); toast.success("Entry deleted"); setDeleteId(null); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-6">
      <PageHeader title="Revenue" subtitle="Manual daily/weekly sales entry. Toast &amp; Clover POS integration coming soon.">
        <ExportButton resource="revenue" params={queryParams} />
        <Button variant="outline" data-testid="import-revenue-button" onClick={() => { setImportResult(null); setImportFile(null); setImportOpen(true); }} className="h-9 gap-2 rounded-full border-[var(--pb-border)] bg-white text-sm font-medium text-[#18344A] hover:bg-[rgba(24,52,74,0.04)]">
          <Upload className="h-4 w-4 text-[#526E7E]" /> Import
        </Button>
        <Button data-testid="add-revenue-button" onClick={openCreate} className="h-9 gap-2 rounded-full bg-[#18344A] text-white hover:bg-[#18344A]/90">
          <Plus className="h-4 w-4" /> Add entry
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard testId="kpi-revenue-total" label="Total revenue" value={fmtCurrency(total)} icon={DollarSign} accent="navy" />
        <KpiCard testId="kpi-revenue-avg" label="Avg / day" value={fmtCurrency(avg)} icon={TrendingUp} accent="blue" />
        <KpiCard testId="kpi-revenue-entries" label="Entries" value={items.length} icon={DollarSign} accent="sand" />
        <KpiCard testId="kpi-revenue-days" label="Days covered" value={new Set(items.map((r) => r.date)).size} icon={TrendingUp} accent="muted" />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <ChartCard title="Revenue by day" testId="chart-revenue-by-day" className="lg:col-span-2">
          {trend.length === 0 ? (
            <EmptyState title="No revenue yet" message="Add an entry to see the trend." icon={DollarSign} />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={trend} margin={{ left: 4, right: 8, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(82,110,126,0.18)" vertical={false} />
                <XAxis dataKey="date" tickFormatter={shortDate} tick={axisStyle} axisLine={false} tickLine={false} minTickGap={24} />
                <YAxis tickFormatter={(v) => `$${Math.round(v / 1000)}k`} tick={axisStyle} axisLine={false} tickLine={false} width={48} />
                <Tooltip content={<ChartTooltip valueFormatter={(v) => fmtCurrency(v)} />} labelFormatter={shortDate} cursor={{ fill: "rgba(183,165,142,0.12)" }} />
                <Bar dataKey="revenue" name="Revenue" radius={[4, 4, 0, 0]} fill={CHART_COLORS.revenue} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Revenue by category" testId="chart-revenue-by-category">
          {byCategory.length === 0 ? (
            <EmptyState title="No revenue yet" message="Includes dining, events & lodging." icon={DollarSign} />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={byCategory} dataKey="value" nameKey="name" cx="50%" cy="45%" innerRadius={52} outerRadius={82} paddingAngle={2}>
                  {byCategory.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip content={<ChartTooltip valueFormatter={(v) => fmtCurrency(v)} />} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      <Card className="overflow-hidden rounded-[14px] border-[var(--pb-border)] bg-white shadow-[var(--pb-shadow-sm)]">
        <CardHeader className="pb-2"><CardTitle className="font-display text-base font-semibold text-[#18344A]">Entry history</CardTitle></CardHeader>
        <CardContent className="px-0">
          {loading ? (
            <div className="p-4"><BlockSkeleton className="h-64" /></div>
          ) : items.length === 0 ? (
            <EmptyState title="No entries" message="Add your first revenue entry." icon={DollarSign} />
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="revenue-table">
                <TableHeader>
                  <TableRow className="bg-[rgba(24,52,74,0.04)]">
                    <TableHead>Date</TableHead>
                    {isCorporate && <TableHead>Brand</TableHead>}
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((r) => (
                    <TableRow key={r.id} className="hover:bg-[rgba(183,165,142,0.1)]">
                      <TableCell className="text-sm font-medium text-[#30383C]">{fmtDate(r.date)}</TableCell>
                      {isCorporate && <TableCell className="text-sm">{brandName[r.brand_id] || "\u2014"}</TableCell>}
                      <TableCell className="text-right font-num font-medium text-[#18344A]">{fmtCurrencyCents(r.amount)}</TableCell>
                      <TableCell className="text-sm capitalize text-[rgba(48,56,60,0.7)]">{r.category}</TableCell>
                      <TableCell className="max-w-[220px] truncate text-sm text-[rgba(48,56,60,0.6)]">{r.notes || "\u2014"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" data-testid={`edit-revenue-${r.id}`} onClick={() => openEdit(r)} className="h-8 w-8 text-[#526E7E]"><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" data-testid={`delete-revenue-${r.id}`} onClick={() => setDeleteId(r.id)} className="h-8 w-8 text-[#9a3a3a]"><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-[#18344A]">{editingId ? "Edit revenue entry" : "Add revenue entry"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="revenue-form">
            {!editingId && <BrandSelectField value={form.brand_id} onChange={set("brand_id")} />}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Date</Label><Input type="date" required data-testid="revenue-date" value={form.date} onChange={(e) => set("date")(e.target.value)} /></div>
              <div className="space-y-1.5">
                <Label>Amount (USD)</Label>
                <Input type="number" step="0.01" min="0" required data-testid="revenue-entry-amount-input" value={form.amount} onChange={(e) => set("amount")(e.target.value)} placeholder="0.00" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={set("category")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => set("notes")(e.target.value)} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="revenue-form-submit" className="bg-[#18344A] text-white hover:bg-[#18344A]/90">{saving ? "Saving\u2026" : editingId ? "Save" : "Add entry"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete revenue entry?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-revenue" onClick={confirmDelete} className="bg-[#9a3a3a] text-white hover:bg-[#9a3a3a]/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-[#18344A]">Import revenue</DialogTitle></DialogHeader>
          <div className="space-y-4" data-testid="import-revenue-dialog">
            <p className="text-sm text-[rgba(48,56,60,0.7)]">
              Upload a CSV or Excel file. Required columns: <span className="font-medium text-[#18344A]">date</span> and <span className="font-medium text-[#18344A]">amount</span>.
              Optional: <span className="font-medium text-[#18344A]">category</span> (total, food, beverage, events, lodging, other), <span className="font-medium text-[#18344A]">brand</span>, and <span className="font-medium text-[#18344A]">notes</span>.
            </p>

            <button type="button" onClick={downloadTemplate} data-testid="download-template-button" className="inline-flex items-center gap-2 text-sm font-medium text-[#526E7E] underline-offset-2 hover:underline">
              <FileDown className="h-4 w-4" /> Download CSV template
            </button>

            {isCorporate && (
              <div className="space-y-1.5">
                <Label>Default brand (for rows without a "brand" column)</Label>
                <Select value={importBrand} onValueChange={setImportBrand}>
                  <SelectTrigger data-testid="import-default-brand"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Use the "brand" column in the file</SelectItem>
                    {nonCorpBrands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1.5">
              <Label>File</Label>
              <Input type="file" accept=".csv,.xlsx,.xls" data-testid="import-file-input" onChange={(e) => setImportFile(e.target.files?.[0] || null)} />
            </div>

            {importResult && (
              <div data-testid="import-result" className="rounded-[10px] border border-[var(--pb-border)] bg-[rgba(24,52,74,0.03)] p-3 text-sm">
                <div className="font-medium text-[#18344A]">Imported {importResult.inserted} · Skipped {importResult.skipped}</div>
                {importResult.errors?.length > 0 && (
                  <ul className="mt-2 max-h-40 list-disc space-y-0.5 overflow-y-auto pl-5 text-xs text-[#9a3a3a]">
                    {importResult.errors.map((er, i) => <li key={i}>{er}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setImportOpen(false)}>Close</Button>
            <Button type="button" disabled={importing || !importFile} data-testid="run-import-button" onClick={runImport} className="bg-[#18344A] text-white hover:bg-[#18344A]/90">
              {importing ? "Importing\u2026" : "Import file"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
